import { replaceEqualDeep } from '@tanstack/query-core'
import { useRef } from 'react'

/**
 * A hook that maintains stable references for data structures using structural diffing.
 *
 * This hook uses deep structural comparison to preserve object/array references
 * when their content hasn't changed, which helps prevent unnecessary re-renders
 * in React components.
 *
 * @example
 * ```tsx
 * const data = useStructuralDiff({
 *   users: filteredUsers,
 *   posts: filteredPosts
 * })
 * // If filteredUsers hasn't changed, data.users will have the same reference
 * ```
 *
 * @param value - The value to apply structural diffing to
 * @returns The value with stable references for unchanged parts
 */
export function useStructuralDiff<T>(value: T): T {
    const previousRef = useRef<T>(value)

    const result = replaceEqualDeep(previousRef.current, value)
    previousRef.current = result

    return result
}
